import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '../../api'

/** Runtime Stripe config for the web checkout (publishable key, never the secret). */
export interface BillingConfig {
  publishable_key: string
  amount_cents: number
  currency: string
}

/** Response from creating the PelviaCare+ PaymentIntent. */
export interface PelviaCareIntentResponse {
  client_secret: string
  amount_cents: number
  currency: string
  publishable_key: string
}

export function useBillingConfig() {
  return useQuery({
    queryKey: ['billing', 'config'],
    queryFn: () => api.get<BillingConfig>('/api/billing/config'),
    staleTime: 5 * 60_000,
  })
}

/** PelviaCare+ membership state for the patient (drives the homepage card). */
export interface PelviaCareStatusResponse {
  /** True once a paid/fulfilled order exists — set the instant payment confirms. */
  is_member: boolean
  order_status: 'pending' | 'paid' | 'fulfilled' | 'failed' | null
}

export function usePelviaCareStatus() {
  return useQuery({
    queryKey: ['billing', 'pelviacare-status'],
    queryFn: () => api.get<PelviaCareStatusResponse>('/api/billing/pelviacare/status'),
    staleTime: 30_000,
  })
}

/**
 * Create the $300 PelviaCare+ PaymentIntent. The response carries the
 * publishable key so the checkout can `loadStripe()` without a build-time var.
 *
 * Errors are surfaced as ApiError: a 409 with `missing_fields` means demographics
 * are needed first (use `missingDemographicsFrom` from useAkute); other 409s mean
 * already paid / already enrolled.
 */
export function useCreatePelviaCareIntent() {
  return useMutation({
    mutationFn: () => api.post<PelviaCareIntentResponse>('/api/billing/pelviacare/intent'),
  })
}

/** Result of finalizing the payment: drives whether the Akute send has kicked off. */
export interface PelviaCareFinalizeResponse {
  status: 'processing' | 'fulfilled' | 'pending'
}

/**
 * Finalize after the Payment Element confirms client-side. Re-verifies the
 * PaymentIntent server-side and kicks off fulfillment (note → Akute) right away
 * rather than waiting on the webhook — and is the path that fires in local dev
 * where the Stripe webhook isn't delivered. Idempotent (the webhook is the
 * durable backstop), so a failure here is non-fatal to the success screen.
 */
export function useFinalizePelviaCare() {
  return useMutation({
    mutationFn: (paymentIntentId: string) =>
      api.post<PelviaCareFinalizeResponse>('/api/billing/pelviacare/finalize', {
        payment_intent_id: paymentIntentId,
      }),
  })
}
